'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import InnerPageHeader from '@/components/InnerPageHeader';
import Toast from '@/components/Toast';

export default function AdminDashboard() {
  const router = useRouter();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  
  // Data states
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalTrainers: 0,
    totalGyms: 0,
    pendingTrainers: 0,
    pendingGyms: 0,
  });
  const [users, setUsers] = useState<any[]>([]);
  const [trainers, setTrainers] = useState<any[]>([]);
  const [gyms, setGyms] = useState<any[]>([]);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (!userData) {
      router.push('/admin/login');
      return;
    }

    const parsedUser = JSON.parse(userData);
    if (parsedUser.role !== 'admin') {
      router.push('/login');
      return;
    }

    setUser(parsedUser);
    fetchDashboardData();
  }, [router]);

  const fetchDashboardData = async () => {
    try {
      // Fetch stats
      const statsResponse = await fetch('/api/admin/stats');
      const statsData = await statsResponse.json();
      if (statsResponse.ok) {
        setStats(statsData.stats);
      }

      // Fetch users
      const usersResponse = await fetch('/api/admin/users');
      const usersData = await usersResponse.json();
      if (usersResponse.ok) {
        setUsers(usersData.users);
      }

      // Fetch trainers
      const trainersResponse = await fetch('/api/admin/trainers');
      const trainersData = await trainersResponse.json();
      if (trainersResponse.ok) {
        setTrainers(trainersData.trainers);
      }

      // Fetch gyms
      const gymsResponse = await fetch('/api/admin/gyms');
      const gymsData = await gymsResponse.json();
      if (gymsResponse.ok) {
        setGyms(gymsData.gyms);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    router.push('/admin/login');
  };

  const handleApproveTrainer = async (trainerId: string) => {
    try {
      const response = await fetch('/api/admin/trainer/approve', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ trainerId, status: 'approved' })
      });

      if (response.ok) {
        setToast({ message: 'Trainer approved successfully!', type: 'success' });
        fetchDashboardData();
      } else {
        setToast({ message: 'Failed to approve trainer', type: 'error' });
      }
    } catch (error) {
      setToast({ message: 'Failed to approve trainer', type: 'error' });
    }
  };

  const handleRejectTrainer = async (trainerId: string) => {
    try {
      const response = await fetch('/api/admin/trainer/approve', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ trainerId, status: 'rejected' })
      });

      if (response.ok) {
        setToast({ message: 'Trainer rejected', type: 'success' });
        fetchDashboardData();
      } else {
        setToast({ message: 'Failed to reject trainer', type: 'error' });
      }
    } catch (error) {
      setToast({ message: 'Failed to reject trainer', type: 'error' });
    }
  };

  const handleApproveGym = async (gymId: string) => {
    try {
      const response = await fetch('/api/admin/gym/approve', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ gymId, status: 'approved' })
      });

      if (response.ok) {
        setToast({ message: 'Gym approved successfully!', type: 'success' });
        fetchDashboardData();
      } else {
        setToast({ message: 'Failed to approve gym', type: 'error' });
      }
    } catch (error) {
      setToast({ message: 'Failed to approve gym', type: 'error' });
    }
  };

  const handleRejectGym = async (gymId: string) => {
    try {
      const response = await fetch('/api/admin/gym/approve', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ gymId, status: 'rejected' })
      });

      if (response.ok) {
        setToast({ message: 'Gym rejected', type: 'success' });
        fetchDashboardData();
      } else {
        setToast({ message: 'Failed to reject gym', type: 'error' });
      }
    } catch (error) {
      setToast({ message: 'Failed to reject gym', type: 'error' });
    }
  };

  const handleSuspendUser = async (userId: string) => {
    try {
      const response = await fetch('/api/admin/users/manage', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, action: 'suspend' })
      });

      if (response.ok) {
        setToast({ message: 'User suspended successfully', type: 'success' });
        fetchDashboardData();
      } else {
        setToast({ message: 'Failed to suspend user', type: 'error' });
      }
    } catch (error) {
      setToast({ message: 'Failed to suspend user', type: 'error' });
    }
  };

  const handleActivateUser = async (userId: string) => {
    try {
      const response = await fetch('/api/admin/users/manage', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, action: 'activate' })
      });

      if (response.ok) {
        setToast({ message: 'User activated successfully', type: 'success' });
        fetchDashboardData();
      } else {
        setToast({ message: 'Failed to activate user', type: 'error' });
      }
    } catch (error) {
      setToast({ message: 'Failed to activate user', type: 'error' });
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!confirm('Are you sure you want to delete this user? This action can be restored later.')) {
      return;
    }

    try {
      const response = await fetch('/api/admin/users/manage', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, action: 'delete' })
      });

      if (response.ok) {
        setToast({ message: 'User deleted successfully', type: 'success' });
        fetchDashboardData();
      } else {
        setToast({ message: 'Failed to delete user', type: 'error' });
      }
    } catch (error) {
      setToast({ message: 'Failed to delete user', type: 'error' });
    }
  };

  const handleLoginAsUser = async (userId: string, userEmail: string) => {
    if (!confirm(`Are you sure you want to login as ${userEmail}? You will be able to access their account.`)) {
      return;
    }

    try {
      const response = await fetch('/api/admin/users/impersonate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Store original admin token BEFORE overwriting
        const currentAdminToken = localStorage.getItem('authToken');
        localStorage.setItem('originalAdminToken', currentAdminToken || '');
        
        // Store impersonation token and user data
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        localStorage.setItem('isImpersonating', 'true');
        
        console.log('👑 Impersonation setup:', {
          originalToken: !!currentAdminToken,
          newToken: !!data.token,
          user: data.user.email
        });
        
        setToast({ 
          message: `Successfully logged in as ${data.user.fullName}. Click "Back to Admin Dashboard" to switch back.`, 
          type: 'success' 
        });

        // Redirect based on user role
        setTimeout(() => {
          if (data.user.role === 'trainer') {
            window.location.href = '/dashboard/trainer';
          } else if (data.user.role === 'gym') {
            window.location.href = '/dashboard/gym';
          } else {
            window.location.href = '/dashboard';
          }
        }, 1500);
      } else {
        const errorData = await response.json();
        setToast({ message: errorData.error || 'Failed to login as user', type: 'error' });
      }
    } catch (error) {
      setToast({ message: 'Failed to login as user', type: 'error' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F6EFED] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#8a7296] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="font-['Archivo',sans-serif] text-[16px] text-[#6e6672]">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6EFED]">
      <InnerPageHeader />

      <section className="py-12">
        <div className="max-w-[1380px] mx-auto px-[20px]">
          <div className="grid md:grid-cols-[280px_1fr] gap-8">
            
            {/* Sidebar */}
            <aside className="bg-white rounded-[24px] p-6 h-fit shadow-sm">
              {/* Admin Profile */}
              <div className="text-center mb-6">
                <div className="w-24 h-24 mx-auto mb-4 bg-[#8A7296]/20 rounded-full flex items-center justify-center">
                  <svg className="w-12 h-12 text-[#8A7296]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                  </svg>
                </div>
                <h3 className="font-['Gotham_Rounded',sans-serif] text-[18px] font-bold text-[#2E2A3A]">
                  {user?.fullName}
                </h3>
                <p className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672]">
                  {user?.email}
                </p>
                <div className="mt-3">
                  <span className="inline-block px-3 py-1 bg-red-100 text-red-700 rounded-full font-['Archivo',sans-serif] text-[12px] font-medium">
                    Super Admin
                  </span>
                </div>
              </div>

              {/* Navigation */}
              <nav className="space-y-2">
                <button
                  onClick={() => setActiveTab('overview')}
                  className={`w-full text-left px-4 py-3 rounded-lg font-['Archivo',sans-serif] text-[16px] transition-colors ${
                    activeTab === 'overview'
                      ? 'bg-[#8A7296] text-white'
                      : 'text-[#2E2A3A] hover:bg-[#F6EFED]'
                  }`}
                >
                  Overview
                </button>
                <button
                  onClick={() => setActiveTab('users')}
                  className={`w-full text-left px-4 py-3 rounded-lg font-['Archivo',sans-serif] text-[16px] transition-colors ${
                    activeTab === 'users'
                      ? 'bg-[#8A7296] text-white'
                      : 'text-[#2E2A3A] hover:bg-[#F6EFED]'
                  }`}
                >
                  Users
                </button>
                <button
                  onClick={() => setActiveTab('trainers')}
                  className={`w-full text-left px-4 py-3 rounded-lg font-['Archivo',sans-serif] text-[16px] transition-colors ${
                    activeTab === 'trainers'
                      ? 'bg-[#8A7296] text-white'
                      : 'text-[#2E2A3A] hover:bg-[#F6EFED]'
                  }`}
                >
                  Trainers
                </button>
                <button
                  onClick={() => setActiveTab('gyms')}
                  className={`w-full text-left px-4 py-3 rounded-lg font-['Archivo',sans-serif] text-[16px] transition-colors ${
                    activeTab === 'gyms'
                      ? 'bg-[#8A7296] text-white'
                      : 'text-[#2E2A3A] hover:bg-[#F6EFED]'
                  }`}
                >
                  Gyms
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-3 rounded-lg font-['Archivo',sans-serif] text-[16px] text-red-600 hover:bg-red-50 transition-colors"
                >
                  Logout
                </button>
              </nav>
            </aside>

            {/* Main Content */}
            <main>
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div>
                  <h1 className="font-['Gotham_Rounded',sans-serif] text-[36px] font-bold text-[#2E2A3A] mb-8">
                    System Overview
                  </h1>

                  {/* Stats Cards */}
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <div className="bg-white rounded-[20px] p-6 shadow-sm">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                          <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672]">Total Users</p>
                          <p className="font-['Gotham_Rounded',sans-serif] text-[24px] font-bold text-[#2E2A3A]">
                            {stats.totalUsers}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-[20px] p-6 shadow-sm">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                          <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672]">Trainers</p>
                          <p className="font-['Gotham_Rounded',sans-serif] text-[24px] font-bold text-[#2E2A3A]">
                            {stats.totalTrainers}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-[20px] p-6 shadow-sm">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                          <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672]">Gyms</p>
                          <p className="font-['Gotham_Rounded',sans-serif] text-[24px] font-bold text-[#2E2A3A]">
                            {stats.totalGyms}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-[20px] p-6 shadow-sm">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                          <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672]">Pending</p>
                          <p className="font-['Gotham_Rounded',sans-serif] text-[24px] font-bold text-[#2E2A3A]">
                            {stats.pendingTrainers + stats.pendingGyms}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="bg-white rounded-[24px] p-8 shadow-sm">
                    <h2 className="font-['Gotham_Rounded',sans-serif] text-[24px] font-bold text-[#2E2A3A] mb-6">
                      Quick Actions
                    </h2>
                    <div className="grid md:grid-cols-2 gap-4">
                      <button
                        onClick={() => setActiveTab('trainers')}
                        className="p-4 bg-[#f5f2f0] rounded-[16px] text-left hover:bg-[#e5e2e0] transition-colors"
                      >
                        <h3 className="font-['Gotham_Rounded',sans-serif] text-[18px] font-bold text-[#2E2A3A] mb-2">
                          Review Trainers
                        </h3>
                        <p className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672]">
                          {stats.pendingTrainers} pending approvals
                        </p>
                      </button>
                      <button
                        onClick={() => setActiveTab('gyms')}
                        className="p-4 bg-[#f5f2f0] rounded-[16px] text-left hover:bg-[#e5e2e0] transition-colors"
                      >
                        <h3 className="font-['Gotham_Rounded',sans-serif] text-[18px] font-bold text-[#2E2A3A] mb-2">
                          Review Gyms
                        </h3>
                        <p className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672]">
                          {stats.pendingGyms} pending approvals
                        </p>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Users Tab */}
              {activeTab === 'users' && (
                <div>
                  <h1 className="font-['Gotham_Rounded',sans-serif] text-[36px] font-bold text-[#2E2A3A] mb-8">
                    All Users
                  </h1>
                  <div className="bg-white rounded-[24px] p-8 shadow-sm">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-[#d7d7d7]">
                            <th className="text-left py-3 px-4 font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A]">Name</th>
                            <th className="text-left py-3 px-4 font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A]">Email</th>
                            <th className="text-left py-3 px-4 font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A]">Phone</th>
                            <th className="text-left py-3 px-4 font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A]">Role</th>
                            <th className="text-left py-3 px-4 font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A]">Status</th>
                            <th className="text-left py-3 px-4 font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A]">Joined</th>
                            <th className="text-left py-3 px-4 font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A]">Login</th>
                            <th className="text-left py-3 px-4 font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A]">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {users.map((user) => (
                            <tr key={user.id} className="border-b border-[#f5f2f0]">
                              <td className="py-3 px-4 font-['Archivo',sans-serif] text-[16px] text-[#2E2A3A]">
                                {user.fullName}
                              </td>
                              <td className="py-3 px-4 font-['Archivo',sans-serif] text-[16px] text-[#2E2A3A]">
                                {user.email}
                              </td>
                              <td className="py-3 px-4 font-['Archivo',sans-serif] text-[16px] text-[#2E2A3A]">
                                {user.phone}
                              </td>
                              <td className="py-3 px-4">
                                <span className={`px-2 py-1 rounded-full font-['Archivo',sans-serif] text-[12px] font-medium ${
                                  user.role === 'admin' ? 'bg-red-100 text-red-700' :
                                  user.role === 'trainer' ? 'bg-green-100 text-green-700' :
                                  user.role === 'gym' ? 'bg-purple-100 text-purple-700' :
                                  'bg-blue-100 text-blue-700'
                                }`}>
                                  {user.role}
                                </span>
                              </td>
                              <td className="py-3 px-4">
                                <span className={`px-2 py-1 rounded-full font-['Archivo',sans-serif] text-[12px] font-medium ${
                                  user.status === 'active' ? 'bg-green-100 text-green-700' :
                                  user.status === 'suspended' ? 'bg-yellow-100 text-yellow-700' :
                                  user.status === 'deleted' ? 'bg-red-100 text-red-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  {user.status || 'active'}
                                </span>
                              </td>
                              <td className="py-3 px-4 font-['Archivo',sans-serif] text-[16px] text-[#6e6672]">
                                {new Date(user.createdAt).toLocaleDateString()}
                              </td>
                              <td className="py-3 px-4">
                                {user.status === 'active' && user.role !== 'admin' && (
                                  <button
                                    onClick={() => handleLoginAsUser(user.id, user.email)}
                                    className="px-3 py-1 bg-purple-600 text-white font-['Archivo',sans-serif] text-[12px] font-medium rounded-[6px] hover:bg-purple-700 transition-colors"
                                  >
                                    Login as {user.fullName}
                                  </button>
                                )}
                                {user.status !== 'active' && (
                                  <span className="px-3 py-1 bg-gray-200 text-gray-500 font-['Archivo',sans-serif] text-[12px] font-medium rounded-[6px]">
                                    Not Available
                                  </span>
                                )}
                                {user.role === 'admin' && (
                                  <span className="px-3 py-1 bg-red-100 text-red-700 font-['Archivo',sans-serif] text-[12px] font-medium rounded-[6px]">
                                    Admin
                                  </span>
                                )}
                              </td>
                              <td className="py-3 px-4">
                                <div className="flex gap-2">
                                  {user.status === 'active' && (
                                    <>
                                      <button
                                        onClick={() => handleSuspendUser(user.id)}
                                        className="px-3 py-1 bg-yellow-600 text-white font-['Archivo',sans-serif] text-[12px] font-medium rounded-[6px] hover:bg-yellow-700 transition-colors"
                                      >
                                        Suspend
                                      </button>
                                      <button
                                        onClick={() => handleDeleteUser(user.id)}
                                        className="px-3 py-1 bg-red-600 text-white font-['Archivo',sans-serif] text-[12px] font-medium rounded-[6px] hover:bg-red-700 transition-colors"
                                      >
                                        Delete
                                      </button>
                                    </>
                                  )}
                                  {user.status === 'suspended' && (
                                    <button
                                      onClick={() => handleActivateUser(user.id)}
                                      className="px-3 py-1 bg-green-600 text-white font-['Archivo',sans-serif] text-[12px] font-medium rounded-[6px] hover:bg-green-700 transition-colors"
                                    >
                                      Activate
                                    </button>
                                  )}
                                  {user.status === 'deleted' && (
                                    <button
                                      onClick={() => handleActivateUser(user.id)}
                                      className="px-3 py-1 bg-green-600 text-white font-['Archivo',sans-serif] text-[12px] font-medium rounded-[6px] hover:bg-green-700 transition-colors"
                                    >
                                      Restore
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Trainers Tab */}
              {activeTab === 'trainers' && (
                <div>
                  <h1 className="font-['Gotham_Rounded',sans-serif] text-[36px] font-bold text-[#2E2A3A] mb-8">
                    Trainers Management
                  </h1>
                  <div className="bg-white rounded-[24px] p-8 shadow-sm">
                    <div className="space-y-4">
                      {trainers.map((trainer) => (
                        <div key={trainer.id} className="border border-[#d7d7d7] rounded-[16px] p-6">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-4 mb-4">
                                <img
                                  src={trainer.profilePhoto}
                                  alt={trainer.firstName}
                                  className="w-16 h-16 rounded-full object-cover"
                                />
                                <div>
                                  <h3 className="font-['Gotham_Rounded',sans-serif] text-[20px] font-bold text-[#2E2A3A]">
                                    {trainer.firstName} {trainer.lastName}
                                  </h3>
                                  <p className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672]">
                                    {trainer.email} • {trainer.phone}
                                  </p>
                                </div>
                              </div>
                              <div className="grid md:grid-cols-2 gap-4 text-sm">
                                <div>
                                  <p className="font-['Archivo',sans-serif] text-[12px] text-[#6e6672] mb-1">Location</p>
                                  <p className="font-['Archivo',sans-serif] text-[14px] text-[#2E2A3A]">{trainer.location}</p>
                                </div>
                                <div>
                                  <p className="font-['Archivo',sans-serif] text-[12px] text-[#6e6672] mb-1">Experience</p>
                                  <p className="font-['Archivo',sans-serif] text-[14px] text-[#2E2A3A]">{trainer.yearsOfExperience} years</p>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className={`px-3 py-1 rounded-full font-['Archivo',sans-serif] text-[12px] font-medium ${
                                trainer.status === 'approved' ? 'bg-green-100 text-green-700' :
                                trainer.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {trainer.status}
                              </span>
                              {trainer.status === 'pending' && (
                                <>
                                  <button
                                    onClick={() => handleApproveTrainer(trainer.id)}
                                    className="px-4 py-2 bg-green-600 text-white font-['Archivo',sans-serif] text-[14px] font-medium rounded-[8px] hover:bg-green-700 transition-colors"
                                  >
                                    Approve
                                  </button>
                                  <button
                                    onClick={() => handleRejectTrainer(trainer.id)}
                                    className="px-4 py-2 bg-red-600 text-white font-['Archivo',sans-serif] text-[14px] font-medium rounded-[8px] hover:bg-red-700 transition-colors"
                                  >
                                    Reject
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Gyms Tab */}
              {activeTab === 'gyms' && (
                <div>
                  <h1 className="font-['Gotham_Rounded',sans-serif] text-[36px] font-bold text-[#2E2A3A] mb-8">
                    Gyms Management
                  </h1>
                  <div className="bg-white rounded-[24px] p-8 shadow-sm">
                    <div className="space-y-4">
                      {gyms.map((gym) => (
                        <div key={gym.id} className="border border-[#d7d7d7] rounded-[16px] p-6">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-4 mb-4">
                                {gym.photos && gym.photos.length > 0 && (
                                  <img
                                    src={gym.photos[0]}
                                    alt={gym.businessName}
                                    className="w-16 h-16 rounded-full object-cover"
                                  />
                                )}
                                <div>
                                  <h3 className="font-['Gotham_Rounded',sans-serif] text-[20px] font-bold text-[#2E2A3A]">
                                    {gym.businessName || `${gym.firstName}'s Gym`}
                                  </h3>
                                  <p className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672]">
                                    {gym.email} • {gym.phone}
                                  </p>
                                </div>
                              </div>
                              <div className="grid md:grid-cols-2 gap-4 text-sm">
                                <div>
                                  <p className="font-['Archivo',sans-serif] text-[12px] text-[#6e6672] mb-1">Location</p>
                                  <p className="font-['Archivo',sans-serif] text-[14px] text-[#2E2A3A]">{gym.location}</p>
                                </div>
                                <div>
                                  <p className="font-['Archivo',sans-serif] text-[12px] text-[#6e6672] mb-1">Contact Person</p>
                                  <p className="font-['Archivo',sans-serif] text-[14px] text-[#2E2A3A]">{gym.firstName}</p>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className={`px-3 py-1 rounded-full font-['Archivo',sans-serif] text-[12px] font-medium ${
                                gym.status === 'approved' ? 'bg-green-100 text-green-700' :
                                gym.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {gym.status}
                              </span>
                              {gym.status === 'pending' && (
                                <>
                                  <button
                                    onClick={() => handleApproveGym(gym.id)}
                                    className="px-4 py-2 bg-green-600 text-white font-['Archivo',sans-serif] text-[14px] font-medium rounded-[8px] hover:bg-green-700 transition-colors"
                                  >
                                    Approve
                                  </button>
                                  <button
                                    onClick={() => handleRejectGym(gym.id)}
                                    className="px-4 py-2 bg-red-600 text-white font-['Archivo',sans-serif] text-[14px] font-medium rounded-[8px] hover:bg-red-700 transition-colors"
                                  >
                                    Reject
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </main>
          </div>
        </div>
      </section>

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
